import type { Agendamento, EventoHistorico, Instalacao } from '@/data/types';
import { somarDias } from '@/lib/format';

/**
 * Regras de negócio do PAN, num lugar só.
 *
 * No app do monorepo estas regras estavam espalhadas em três lugares diferentes:
 * dentro de `GenericConditionalRender` nos layouts JSON (ex.: desabilitar o
 * botão quando `instalacoes.selected/atualizar === false`), dentro do
 * `AgendamentosApi.create` (validações de versão/data/hora) e implícitas no
 * backend. Aqui elas são funções puras, testáveis, e a interface apenas as
 * consulta — nenhuma tela decide sozinha se pode agendar.
 */

export interface MotivoBloqueio {
  permitido: boolean;
  /** Frase pronta, em PT-BR, que vai para `title`/tooltip/`aria-label`. */
  motivo: string;
  /**
   * `atencao` = algo impede e o usuário talvez precise agir (permissão, bloqueio).
   * `neutro`  = a ação não se aplica, e isso não é um problema (já está em dia).
   * Existe para a interface não pintar de âmbar uma instalação saudável.
   */
  severidade: 'atencao' | 'neutro';
}

const LIBERADO: MotivoBloqueio = { permitido: true, motivo: '', severidade: 'neutro' };

export interface ContextoAgendamento {
  /** Já tem uma atualização 'Programado' ou 'Executando' pendente — vem de `listAgendamentos`. */
  agendamentoPendente?: boolean;
  /** Outra pessoa está com essa instalação retida agora (WebSocket de tempo real). */
  travadaPorOutro?: boolean;
}

/** Pode agendar atualização para esta instalação? */
export function podeAgendar(instalacao: Instalacao | null, contexto?: ContextoAgendamento): MotivoBloqueio {
  if (!instalacao) {
    return { permitido: false, motivo: 'Selecione uma instalação para agendar.', severidade: 'neutro' };
  }
  if (!instalacao.administrador) {
    return {
      permitido: false,
      motivo: `Você tem acesso somente leitura em ${instalacao.nome}. Agendar exige permissão de administrador.`,
      severidade: 'atencao',
    };
  }
  if (contexto?.agendamentoPendente) {
    return {
      permitido: false,
      motivo: `${instalacao.nome} já tem uma atualização programada. Cancele-a antes de agendar outra.`,
      severidade: 'atencao',
    };
  }
  if (contexto?.travadaPorOutro) {
    return {
      permitido: false,
      motivo: `${instalacao.nome} está sendo agendada por outra pessoa agora. Tente novamente em instantes.`,
      severidade: 'atencao',
    };
  }
  if (!instalacao.atualizar) {
    return {
      permitido: false,
      motivo: `${instalacao.nome} já está na versão ${instalacao.versaoAtual}; não há atualização disponível.`,
      severidade: 'neutro',
    };
  }
  return LIBERADO;
}

/** Pode cancelar este agendamento? */
export function podeCancelar(agendamento: Agendamento, instalacao: Instalacao | null): MotivoBloqueio {
  if (agendamento.status === 'Cancelado') {
    return { permitido: false, motivo: 'Este agendamento já está cancelado.', severidade: 'neutro' };
  }
  if (instalacao && !instalacao.administrador) {
    return {
      permitido: false,
      motivo: `Você tem acesso somente leitura em ${instalacao.nome}. Cancelar exige permissão de administrador.`,
      severidade: 'atencao',
    };
  }
  return LIBERADO;
}

/** A instalação está desatualizada em relação à versão estável recomendada? */
export function estaDesatualizada(instalacao: Instalacao): boolean {
  if (!instalacao.versaoEstavelRecomendada || !instalacao.versaoAtual) return false;
  return instalacao.versaoAtual !== instalacao.versaoEstavelRecomendada;
}

/**
 * Ordena o hub (todas as famílias do usuário) para exibição: agrupa cada
 * Controlada logo após sua Controladora, famílias em ordem alfabética pelo nome
 * da âncora (Controladora, ou a própria instalação quando avulsa/sem vínculo
 * revelado). Não muda o CONTEÚDO da lista — só a ordem em que ela é desenhada.
 */
export function ordenarPorFamilia(instalacoes: Instalacao[]): Instalacao[] {
  const porAncora = new Map<string, Instalacao[]>();
  for (const item of instalacoes) {
    const ancoraId = item.controladoraId ?? item.id;
    const grupo = porAncora.get(ancoraId);
    if (grupo) grupo.push(item);
    else porAncora.set(ancoraId, [item]);
  }

  const familias = Array.from(porAncora.entries()).map(([ancoraId, itens]) => {
    const ancora = itens.find((item) => item.id === ancoraId) ?? itens[0];
    const ordenados = [...itens].sort((a, b) => {
      if (a.isControladora !== b.isControladora) return a.isControladora ? -1 : 1;
      return a.nome.localeCompare(b.nome);
    });
    return { nomeAncora: ancora.nome, itens: ordenados };
  });

  familias.sort((a, b) => a.nomeAncora.localeCompare(b.nomeAncora));
  return familias.flatMap((familia) => familia.itens);
}

export interface ResumoEscopo {
  total: number;
  desatualizadas: number;
  emAtencao: number;
  emIncidente: number;
  agendamentosProgramados: number;
  falhasRecentes: number;
}

export function resumirEscopo(
  instalacoes: Instalacao[],
  agendamentos: Agendamento[],
  historico: EventoHistorico[],
): ResumoEscopo {
  return {
    total: instalacoes.length,
    desatualizadas: instalacoes.filter(estaDesatualizada).length,
    emAtencao: instalacoes.filter((item) => item.saude === 'Atencao').length,
    emIncidente: instalacoes.filter((item) => item.saude === 'Incidente').length,
    agendamentosProgramados: agendamentos.filter((item) => item.status === 'Programado').length,
    falhasRecentes: historico.filter((item) => item.status === 'Falha' || item.status === 'Falha Parcial').length,
  };
}

/** Próximos agendamentos programados, do mais próximo para o mais distante. */
export function proximosAgendamentos(agendamentos: Agendamento[], limite = 5): Agendamento[] {
  return agendamentos
    .filter((item) => item.status === 'Programado')
    .sort((a, b) => `${a.data ?? ''}${a.hora}`.localeCompare(`${b.data ?? ''}${b.hora}`))
    .slice(0, limite);
}

/** Índice id → instalação, para resolver nomes em listas de outras entidades. */
export function indexarInstalacoes(instalacoes: Instalacao[]): Map<string, Instalacao> {
  return new Map(instalacoes.map((item) => [item.id, item]));
}

/**
 * Chaveável por ora (o backend tem o mesmo default via
 * `AGENDAMENTO_BLOQUEAR_FIM_DE_SEMANA`) — deve virar configuração restrita por
 * capability num bloco de configurações do PAN, quando essa tela existir.
 */
export const BLOQUEAR_AGENDAMENTO_FIM_DE_SEMANA = true;

export function ehFinalDeSemana(iso: string): boolean {
  const [ano, mes, dia] = iso.split('-').map(Number);
  const diaSemana = new Date(ano, mes - 1, dia).getDay();
  return diaSemana === 0 || diaSemana === 6;
}

/** Primeiro dia útil a partir de (e incluindo) `iso`, respeitando o bloqueio de fim de semana. */
export function proximoDiaUtil(iso: string): string {
  if (!BLOQUEAR_AGENDAMENTO_FIM_DE_SEMANA) return iso;
  let atual = iso;
  while (ehFinalDeSemana(atual)) {
    atual = somarDias(atual, 1);
  }
  return atual;
}

/** Valor de um filtro "Sim/Não" (`'sim' | 'nao' | null`) para o boolean da API/lista. */
export function flagDoFiltro(valor: string | null): boolean | undefined {
  return valor === null ? undefined : valor === 'sim';
}
